import apiClient from '../Request'
import { baseUrls } from '../../const'
import moment from 'moment-timezone'

const apiBaseUrl = baseUrls.commonBaseUrl

const endpoints = {
  nextCycle: '/cycles/next',
  cycleById: id => '/cycles/' + id,
  cycles: '/cycles',
  cyclePredict: id => '/cycles/' + id + '/predict',
  cycleReviews: id => '/cycles/' + id + '/reviews',
  submitReview: id => '/cycles/' + id + '/reviews',
  reasonCodes: '/cycles/reason-codes'
}

function clone(data) {
  return JSON.parse(JSON.stringify(data))
}

function success(data) {
  return {
    data: {
      success: true,
      data: clone(data)
    }
  }
}

function getStoredUser() {
  const user = localStorage.getItem('user')
  return user ? JSON.parse(user) : null
}

function getReviewerId(reviewerId) {
  const user = getStoredUser()
  return reviewerId || (user && user.id) || ''
}

function buildUrl(path) {
  return apiBaseUrl + path
}

function buildQuery(params) {
  return Object.keys(params)
    .filter(
      key =>
        params[key] !== '' && params[key] !== null && params[key] !== undefined
    )
    .map(key => key + '=' + encodeURIComponent(params[key]))
    .join('&')
}

function unwrapResponseData(response) {
  if (!response || response.data === undefined || response.data === null) {
    return {}
  }
  return response.data.data !== undefined ? response.data.data : response.data
}

function toNumber(value, fallback) {
  const normalizedFallback = fallback === undefined ? 0 : fallback
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : normalizedFallback
}

function requireReviewerId(reviewerId) {
  const resolvedReviewerId = getReviewerId(reviewerId)
  if (!resolvedReviewerId) {
    return Promise.reject(
      new Error('Reviewer ID is missing for labelling API requests.')
    )
  }
  return Promise.resolve(resolvedReviewerId)
}

function formatDisplayDate(value) {
  if (!value) {
    return '--'
  }
  const timestamp = parseUtcTimestamp(value)
  if (Number.isNaN(timestamp)) {
    return value
  }
  const date = new Date(timestamp)
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  return year + '-' + month + '-' + day + ' ' + hours + ':' + minutes
}

function formatDuration(seconds) {
  const totalMinutes = Math.max(Math.round(toNumber(seconds, 0) / 60), 0)
  if (totalMinutes < 60) {
    return totalMinutes + ' min'
  }
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  if (!minutes) {
    return hours + ' hr'
  }
  return hours + ' hr ' + minutes + ' min'
}

function parseUtcTimestamp(value) {
  if (value === null || value === undefined || value === '') {
    return NaN
  }
  const utcTime = moment.utc(value).valueOf()
  if (Number.isFinite(utcTime)) {
    return utcTime
  }
  const nativeTime = new Date(value).getTime()
  return Number.isFinite(nativeTime) ? nativeTime : NaN
}

function toSeconds(startDate, endDate) {
  if (!startDate || !endDate) {
    return 0
  }
  const start = parseUtcTimestamp(startDate)
  const end = parseUtcTimestamp(endDate)
  if (Number.isNaN(start) || Number.isNaN(end)) {
    return 0
  }
  return Math.max(Math.round((end - start) / 1000), 0)
}

function normalizeReasonCode(reason) {
  return {
    id: reason.id,
    code: reason.code,
    label: reason.label,
    description: reason.description,
    applicableLabel: reason.applicableLabel,
    sortOrder: reason.sortOrder
  }
}

function normalizeBlock(block, cycleStartDate, durationSec) {
  return {
    blockNumber: block.blockNumber,
    blockStart: block.blockStart,
    blockEnd: block.blockEnd,
    startSec: toSeconds(cycleStartDate, block.blockStart),
    endSec: toSeconds(cycleStartDate, block.blockEnd) || durationSec || 0,
    readingCount: toNumber(block.readingCount, 0),
    meanS1: block.meanS1 || 0,
    meanS2: block.meanS2 || 0,
    maxS1: block.maxS1 || 0,
    maxS2: block.maxS2 || 0,
    minS1: block.minS1 || 0,
    minS2: block.minS2 || 0,
    stdS1: block.stdS1 || 0,
    stdS2: block.stdS2 || 0,
    slopeS1: block.slopeS1 || 0,
    slopeS2: block.slopeS2 || 0
  }
}

function getDurationSeconds(startDate, endDate, fallback = 0) {
  const derivedDuration = toSeconds(startDate, endDate)
  return derivedDuration || toNumber(fallback, 0)
}

function normalizePredictionValue(value) {
  if (typeof value === 'boolean') {
    return value ? 'RED' : 'GREEN'
  }
  if (typeof value === 'number') {
    return value > 0 ? 'RED' : 'GREEN'
  }
  const normalizedValue = String(value || '')
    .trim()
    .toUpperCase()
  if (!normalizedValue) {
    return 'UNKNOWN'
  }
  if (
    ['RED', 'BAD', 'POSITIVE', 'ABNORMAL', '1', 'TRUE'].indexOf(
      normalizedValue
    ) > -1
  ) {
    return 'RED'
  }
  if (
    ['GREEN', 'GOOD', 'NEGATIVE', 'NORMAL', '0', 'FALSE'].indexOf(
      normalizedValue
    ) > -1
  ) {
    return 'GREEN'
  }
  return normalizedValue
}

function fromSeconds(startDate, seconds) {
  const start = parseUtcTimestamp(startDate)
  if (Number.isNaN(start)) {
    return null
  }
  return new Date(start + toNumber(seconds, 0) * 1000).toISOString()
}

function resolvePredictionBlockStart(block, cycleStartDate) {
  if (
    block.blockStart ||
    block.startDate ||
    block.startTime ||
    block.sectionStart
  ) {
    return (
      block.blockStart ||
      block.startDate ||
      block.startTime ||
      block.sectionStart
    )
  }
  if (block.startSec !== undefined && block.startSec !== null) {
    return fromSeconds(cycleStartDate, block.startSec)
  }
  return null
}

function resolvePredictionBlockEnd(block, cycleStartDate, durationSec) {
  if (block.blockEnd || block.endDate || block.endTime || block.sectionEnd) {
    return block.blockEnd || block.endDate || block.endTime || block.sectionEnd
  }
  if (block.endSec !== undefined && block.endSec !== null) {
    return fromSeconds(cycleStartDate, block.endSec)
  }
  if (durationSec) {
    return fromSeconds(cycleStartDate, durationSec)
  }
  return null
}

function normalizePredictionBlock(block, cycleStartDate, durationSec, index) {
  const blockStart = resolvePredictionBlockStart(block, cycleStartDate)
  const blockEnd = resolvePredictionBlockEnd(block, cycleStartDate, durationSec)
  return {
    ...normalizeBlock(
      {
        ...block,
        blockNumber:
          block.blockNumber !== undefined && block.blockNumber !== null
            ? block.blockNumber
            : index + 1,
        blockStart,
        blockEnd
      },
      cycleStartDate,
      durationSec
    ),
    confidence: toNumber(block.confidence, 0),
    autoLabel: block.autoLabel || '',
    ruleFired: block.ruleFired || '',
    blockPositionPct: toNumber(block.blockPositionPct, 0),
    prediction: normalizePredictionValue(
      block.prediction ||
        block.predictedLabel ||
        block.predictedClass ||
        block.label ||
        block.result ||
        block.isBad
    )
  }
}

function normalizePredictionCycle(responseData) {
  const payload = responseData || {}
  const cyclePayload = payload.cycle || payload.padCycle || payload
  const normalizedCycle = normalizeCycle(cyclePayload) || {}
  const cycleStartDate =
    normalizedCycle.startDate ||
    cyclePayload.startDate ||
    cyclePayload.cycleStart ||
    null
  const cycleEndDate =
    normalizedCycle.endDate ||
    cyclePayload.endDate ||
    cyclePayload.cycleEnd ||
    null
  const blocksSource =
    payload.blockPredictions ||
    payload.predictions ||
    payload.blocks ||
    cyclePayload.blocks ||
    []
  const durationSec = getDurationSeconds(
    cycleStartDate,
    cycleEndDate,
    normalizedCycle.durationSec || cyclePayload.duration
  )

  return {
    ...normalizedCycle,
    id: normalizedCycle.id || cyclePayload.padCycleId || cyclePayload.id,
    cycleFeatureId:
      normalizedCycle.cycleFeatureId || cyclePayload.cycleFeatureId || '',
    deviceId: normalizedCycle.deviceId || cyclePayload.deviceId || '',
    patientId: normalizedCycle.patientId || cyclePayload.patientId || '',
    productId: normalizedCycle.productId || cyclePayload.productId || '',
    podId:
      normalizedCycle.podId || cyclePayload.podMac || cyclePayload.podId || '',
    startDate: cycleStartDate,
    endDate: cycleEndDate,
    durationSec,
    durationLabel: normalizedCycle.durationLabel || formatDuration(durationSec),
    blockCount: toNumber(cyclePayload.blockCount, blocksSource.length),
    blocks: Array.isArray(blocksSource)
      ? blocksSource.map((block, index) =>
          normalizePredictionBlock(block, cycleStartDate, durationSec, index)
        )
      : []
  }
}

function normalizeSection(section, cycleStartDate) {
  const startTime = section.sectionStart
  const endTime = section.sectionEnd
  const reasonCodes = Array.isArray(section.reasonCodes)
    ? section.reasonCodes
        .map(item => (typeof item === 'string' ? item : item.reasonCode))
        .filter(item => item)
    : []
  return {
    id: section.id,
    startSec: toSeconds(cycleStartDate, startTime),
    endSec: toSeconds(cycleStartDate, endTime),
    startTime,
    endTime,
    sensor: section.sensor || 'BOTH',
    reasonCodes,
    note: section.notes || ''
  }
}

function normalizeReview(review, cycleStartDate) {
  if (!review) {
    return null
  }
  return {
    id: review.id,
    cycleId: review.padCycleId,
    reviewerId: review.reviewerId,
    reviewerName: review.reviewerName,
    label: review.cycleLabel,
    status: review.status,
    notes: review.notes,
    submittedAt: formatDisplayDate(review.reviewDate),
    regions: (review.sections || []).map(section =>
      normalizeSection(section, cycleStartDate)
    )
  }
}

function normalizeCycle(cycle) {
  if (!cycle) {
    return null
  }
  const durationSec = Math.round(toNumber(cycle.duration, 0))
  return {
    id: cycle.padCycleId,
    cycleFeatureId: cycle.cycleFeatureId,
    deviceId: cycle.deviceId,
    patientId: cycle.patientId,
    productId: cycle.productId,
    podId: cycle.podMac,
    startDate: cycle.startDate,
    endDate: cycle.endDate,
    startedAt: formatDisplayDate(cycle.startDate),
    endedAt: formatDisplayDate(cycle.endDate),
    durationSec,
    durationLabel: formatDuration(durationSec),
    readingCount: toNumber(cycle.readingCount, 0),
    disconnectCount: toNumber(cycle.disconnectCount, 0),
    autoLabel: cycle.autoLabel,
    ruleFired: cycle.ruleFired,
    baselineS1: toNumber(cycle.baselineS1, 0),
    baselineS2: toNumber(cycle.baselineS2, 0),
    terminalMaxS1: toNumber(cycle.terminalMaxS1, 0),
    terminalMaxS2: toNumber(cycle.terminalMaxS2, 0),
    terminalMeanS1: toNumber(cycle.terminalMeanS1, 0),
    terminalMeanS2: toNumber(cycle.terminalMeanS2, 0),
    terminalStdS1: toNumber(cycle.terminalStdS1, 0),
    terminalStdS2: toNumber(cycle.terminalStdS2, 0),
    terminalSlopeS1: toNumber(cycle.terminalSlopeS1, 0),
    terminalSlopeS2: toNumber(cycle.terminalSlopeS2, 0),
    endMaxS1: toNumber(cycle.endMaxS1, 0),
    endMaxS2: toNumber(cycle.endMaxS2, 0),
    endMeanS1: toNumber(cycle.endMeanS1, 0),
    endMeanS2: toNumber(cycle.endMeanS2, 0),
    endStdS1: toNumber(cycle.endStdS1, 0),
    endStdS2: toNumber(cycle.endStdS2, 0),
    deltaS1: toNumber(cycle.deltaS1, 0),
    deltaS2: toNumber(cycle.deltaS2, 0),
    maxS1: toNumber(cycle.maxS1, 0),
    maxS2: toNumber(cycle.maxS2, 0),
    reviewCount: toNumber(cycle.reviewCount, 0),
    badRegionCount: toNumber(cycle.badRegionCount, 0),
    blocks: (cycle.blocks || []).map(block =>
      normalizeBlock(block, cycle.startDate, durationSec)
    ),
    reviewStatus: cycle.isReviewed ? 'reviewed' : 'unreviewed',
    reviewedBy: cycle.reviewedBy || '',
    reviewedAt: formatDisplayDate(cycle.reviewedAt),
    existingReview: normalizeReview(cycle.existingReview, cycle.startDate)
  }
}

function normalizeCycleListResponse(response) {
  const payload = unwrapResponseData(response)
  return {
    items: payload.items.map(item =>
      normalizeCycle({
        padCycleId: item.padCycleId,
        deviceId: item.deviceId,
        startDate: item.startDate,
        endDate: item.endDate,
        duration: item.duration,
        isReviewed: item.isReviewed,
        reviewCount: item.reviewCount,
        badRegionCount: item.badRegionCount,
        reviewedBy: item.reviewedBy,
        reviewedAt: item.reviewedAt
      })
    ),
    totalCount: toNumber(payload.totalCount, 0),
    page: toNumber(payload.page, 1),
    pageSize: toNumber(payload.pageSize, 20)
  }
}

function normalizeCycleReviewsResponse(response, cycleStartDate) {
  const payload = unwrapResponseData(response)
  return payload.reviews
    .map(item => normalizeReview(item, cycleStartDate))
    .filter(item => item)
}

function toReviewRequest(payload) {
  return {
    cycleLabel: payload.cycleLabel,
    notes: payload.notes || '',
    sections: payload.sections
  }
}

export default {
  getEndpoints() {
    return endpoints
  },
  GetNextCycle(reviewerId) {
    return requireReviewerId(reviewerId).then(resolvedReviewerId =>
      apiClient
        .get(
          buildUrl(
            endpoints.nextCycle +
              '?' +
              buildQuery({ reviewerId: resolvedReviewerId })
          )
        )
        .then(response => {
          const payload = unwrapResponseData(response)
          if (payload.hasNext === false || !payload.cycle) {
            return success(null)
          }
          return success(normalizeCycle(payload.cycle))
        })
        .catch(error => {
          if (error.response && error.response.status === 404) {
            return success(null)
          }
          return Promise.reject(error)
        })
    )
  },
  GetCycleById(id, reviewerId) {
    return requireReviewerId(reviewerId).then(resolvedReviewerId =>
      apiClient
        .get(
          buildUrl(
            endpoints.cycleById(id) +
              '?' +
              buildQuery({ reviewerId: resolvedReviewerId })
          )
        )
        .then(response => success(normalizeCycle(unwrapResponseData(response))))
    )
  },
  GetCycles(filter, hasBadRegions, reviewerId, page, pageSize) {
    return requireReviewerId(reviewerId).then(resolvedReviewerId =>
      apiClient
        .get(
          buildUrl(
            endpoints.cycles +
              '?' +
              buildQuery({
                reviewerId: resolvedReviewerId,
                status: filter || 'all',
                hasBadRegions,
                page: page || 1,
                pageSize: pageSize || 20
              })
          )
        )
        .then(response => success(normalizeCycleListResponse(response)))
    )
  },
  SubmitReview(cycleId, payload) {
    return requireReviewerId(payload && payload.reviewerId).then(
      resolvedReviewerId =>
        apiClient
          .post(
            buildUrl(
              endpoints.submitReview(cycleId) +
                '?' +
                buildQuery({ reviewerId: resolvedReviewerId })
            ),
            toReviewRequest(payload)
          )
          .then(response =>
            success(
              normalizeReview(
                unwrapResponseData(response),
                payload.cycleStartDate
              )
            )
          )
    )
  },
  GetCycleReviews(cycleId, cycleStartDate, reviewerId) {
    return requireReviewerId(reviewerId).then(resolvedReviewerId =>
      apiClient
        .get(
          buildUrl(
            endpoints.cycleReviews(cycleId) +
              '?' +
              buildQuery({ reviewerId: resolvedReviewerId })
          )
        )
        .then(response =>
          success(normalizeCycleReviewsResponse(response, cycleStartDate))
        )
    )
  },
  GetCyclePrediction(cycleId) {
    return apiClient
      .post(buildUrl(endpoints.cyclePredict(cycleId)))
      .then(response =>
        success(normalizePredictionCycle(unwrapResponseData(response)))
      )
  },
  GetReasonCodes() {
    return apiClient
      .get(buildUrl(endpoints.reasonCodes))
      .then(response =>
        success((unwrapResponseData(response) || []).map(normalizeReasonCode))
      )
  }
}
